import { useEffect, useRef } from 'react';
import { TicketApi } from '@/lib/TicketApi';

interface UseLeaveCartProps {
    ticketApi: TicketApi | null;
    cartId: string | null;
    hasReservedProducts: boolean;
}

export const useLeaveCart = ({ ticketApi, cartId, hasReservedProducts }: UseLeaveCartProps) => {
    const hasReservedProductsRef = useRef(hasReservedProducts);
    const cartIdRef = useRef(cartId);
    const ticketApiRef = useRef(ticketApi);

    // Update refs when values change
    useEffect(() => {
        hasReservedProductsRef.current = hasReservedProducts;
    }, [hasReservedProducts]);

    useEffect(() => {
        cartIdRef.current = cartId;
    }, [cartId]);

    useEffect(() => {
        ticketApiRef.current = ticketApi;
    }, [ticketApi]);

    useEffect(() => {
        const handleLeaveCart = () => {
            const currentTicketApi = ticketApiRef.current;
            const currentCartId = cartIdRef.current;
            const currentHasReservedProducts = hasReservedProductsRef.current;

            if (currentTicketApi && currentCartId && currentHasReservedProducts) {
                // Try multiple approaches for maximum reliability
                
                // 1. Image pixel trick (most reliable for GET)
                const img = new Image();
                const baseUrl = (currentTicketApi as unknown as { baseUrl: string }).baseUrl;
                const shopSlug = (currentTicketApi as unknown as { shopSlug: string }).shopSlug;
                const imgUrl = `${baseUrl}/shop/${shopSlug}/cart/${currentCartId}/leave-cart?_=${Date.now()}`;
                img.src = imgUrl;
                
                // 2. Also try the sendBeacon/fetch approach
                currentTicketApi.leaveCart(currentCartId);
            }
        };

        const handleBeforeUnload = () => {
            handleLeaveCart();
        };

        const handlePageHide = () => {
            handleLeaveCart();
        };

        // Listen for beforeunload and pagehide events
        // These fire when the tab/window is actually being closed or navigated away from
        // Not when just switching tabs (which would be bad UX)
        window.addEventListener('beforeunload', handleBeforeUnload);
        window.addEventListener('pagehide', handlePageHide);

        // Cleanup function
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            window.removeEventListener('pagehide', handlePageHide);
        };
    }, []); // Empty dependency array since we use refs for current values
};
